import { yupResolver } from '@hookform/resolvers/yup'
import { filter, map } from 'lodash-es'
import { ChangeEvent, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import Loader from '../../../common/components/Loader'
import { Photo } from '../../../common/models'
import { uploadImage } from '../../../core/services/upload-image'
import {
  deletePhoto,
  getPhotos,
  publishPhoto,
  savePhoto,
} from '../../../core/services/video'
import { AlertRatio } from '@/components/AlertRatio'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'

export interface IPhotoValues {
  publishAt?: number | null
  published?: boolean
  photos?: string[]
}

export interface MediaPhotoCreateModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
  setPhotos: (photos: Photo[]) => void
  photo?: Photo
  setLoading: (l: boolean) => void
}

export const MediaPhotoCreateModal = (
  props: MediaPhotoCreateModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    page,
    size,
    setTotalElemenets,
    setPhotos,
    photo,
    setLoading,
  } = props
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [confirmDeletePic, setConfirmDeletePic] = useState<boolean>(false)
  const [albumPic, setAlbumPic] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [images, setImages] = useState<string[]>([])
  const [imageNames, setImageNames] = useState<string[]>([])
  const [keys, setKeys] = useState<string[]>([])
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const { editionId } = useParams()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    photos: yup.array(),
    published: yup.boolean(),
    publishAt: yup.number().nullable(),
  })

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm<IPhotoValues>({
    mode: 'onChange',
    resolver: yupResolver<IPhotoValues>(schema),
    defaultValues: {
      photos: [],
      published: false,
      publishAt: null,
    },
  })

  const imageWatch = watch('photos')

  const handleDelete = () => {
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        if (photo) {
          const { status } = await deletePhoto(editionId as string, [photo?.id])
          const response = await getPhotos(editionId as string, {
            page: page - 1,
            size,
          })
          setTotalElemenets(response.data.totalElements)
          setPhotos(response.data.content)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
    handleClose()
  }

  const handleClose = () => {
    setInvalidPhoto(false)
    setIsErrorRatio(false)
    setShowCreateModal(false)
    setKeys([])
    setImages([])
    setImageNames([])
    reset()
  }

  const submit = (values: IPhotoValues) => {
    // prettier-ignore
    (async () => {
      setIsSubmitting(true)
      setLoading(true)
      try {
        const { status } = await savePhoto(editionId as string, keys)
        const response = await getPhotos(editionId as string, {
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        if (values.published) {
          const ids: string[] = []
          const temp = filter(response.data.content, content => keys.includes(content.link.substring(content.link.lastIndexOf('/') + 1)))
          setPhotos(response.data.content)
          map(temp, img => {
            ids.push(img.id)
          })
          await publishPhoto(editionId as string, ids)
          const response2 = await getPhotos(editionId as string, {
            page: page - 1,
            size,
          })
          setPhotos(response2.data.content)
        } else {
          setPhotos(response.data.content)
        }

        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
        setIsSubmitting(false)
      }
      handleClose()
    })()
  }

  const deletePic = (index: number) => {
    setImages(filter(images, (img) => images[index] !== img))
    setImageNames(filter(imageNames, (name) => imageNames[index] !== name))
    setKeys(filter(keys, (k) => keys[index] !== k))
    if (imageWatch) {
      imageWatch.splice(index, 1)
      setValue(`photos`, imageWatch)
    }
  }

  return (
    <>
      {isUploading && <Loader />}
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Photo Gallery"
              icon="play"
              subtitle={photo ? 'Edit Photo' : 'Add New Photo'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || isUploading}
              form="media-photo-form"
              type="submit"
              variant="primary"
              loading={isSubmitting || isUploading}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="media-photo-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          {map(images, (image, index) => (
            <FormUploadPreview
              key={`${image}-${keys[index]}`}
              url={image}
              imageName={imageNames[index] || keys[index]}
              isUploading={false}
              onRemove={() => {
                setConfirmDeletePic(true)
                setAlbumPic(index)
              }}
              label="Image (1020x660)"
              previewLabel="Image (1020x660)"
              control={<></>}
            />
          ))}

          <FormUploadPreview
            isUploading={isUploading}
            url=""
            label="Image (1020x660)"
            control={
              <Input
                multiple
                accept="image/*"
                isInvalid={
                  !!errors?.photos?.message || invalidPhoto || isErrorRatio
                }
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const files = e?.target?.files
                  if (files) {
                    setInvalidPhoto(false)
                    setIsErrorRatio(false)
                    const tempkeys: string[] = []
                    const img: string[] = []
                    const names: string[] = []
                    for (let i = 0; i < files.length; i++) {
                      if (files[i].size > 1_000_000) {
                        setInvalidPhoto(true)
                        setIsError(true)
                        setShowMessageBox(true)
                        setMessage(
                          files.length > 1
                            ? 'Some photos are too large (1 MB)'
                            : 'The photo is too large (1 MB)',
                        )
                        return
                      }
                    }
                    try {
                      map(files, async (file, index) => {
                        const data = await uploadImage(
                          file,
                          setIsUploading,
                          setIsError,
                          setShowMessageBox,
                          setMessage,
                          setIsErrorRatio,
                          'PHOTO_GALLERY',
                          false,
                        )
                        if (data) {
                          tempkeys.push(data.photoKey)
                          img.push(data.photoUrl)
                          names.push(file.name)
                          setValue(
                            `photos.${index + images.length}`,
                            data?.photoUrl,
                            {
                              shouldValidate: true,
                            },
                          )
                          setKeys([...keys, ...tempkeys])
                          setImageNames([...imageNames, ...names])
                          setImages([...images, ...img])
                        }
                      })
                    } catch (e) {
                      if (e instanceof Error) {
                        setIsError(true)
                        setShowMessageBox(true)
                        setMessage(e.message)
                        setErrorStatus(
                          (e as { response?: { status?: number } })?.response
                            ?.status,
                        )
                      } else {
                        console.error(e)
                      }
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
          />

          {isErrorRatio && <AlertRatio />}

          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={confirmDeletePic}
        setDeleteConfirmationModal={setConfirmDeletePic}
        deleteTitle={'Photo'}
        deleteFunc={() => deletePic(albumPic)}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Photo'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
